// Razorpay configuration for international payments

export interface RazorpayConfig {
  key: string;
  currency: string;
  name: string;
  description: string;
  image?: string;
  order_id?: string;
  prefill: {
    name?: string;
    email?: string;
    contact?: string;
  };
  theme: {
    color: string;
  };
  modal: {
    ondismiss: () => void;
  };
  handler: (response: RazorpayResponse) => void;
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id?: string; // Optional for order-based payments
  razorpay_subscription_id?: string; // For subscription-based payments
  razorpay_signature: string;
}

export interface PlanPricing {
  plan_id: string;
  currency: string;
  amount: number;
  display_amount: string;
}

// Razorpay public key (should be loaded from environment)
export const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID || '';

// Supported currencies for international payments (USD only)
export const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
] as const;

export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number]['code'];

// Plan pricing in USD only
export const PLAN_PRICING: Record<string, Record<SupportedCurrency, { monthly: number; yearly: number }>> = {
  starter: {
    USD: { monthly: 15, yearly: 250 },
  },
  pro: {
    USD: { monthly: 25, yearly: 950 },
  },
  enterprise: {
    USD: { monthly: 45, yearly: 1900 },
  },
};

// Default Razorpay configuration
export const getDefaultRazorpayConfig = (
  amount: number,
  currency: SupportedCurrency,
  userEmail: string,
  userName?: string
): Partial<RazorpayConfig> => ({
  key: RAZORPAY_KEY_ID,
  currency,
  name: 'Dytafly',
  description: 'Natural Language to SQL Platform',
  image: '/logo.svg', // Dytafly logo
  prefill: {
    name: userName,
    email: userEmail,
  },
  theme: {
    color: '#3B82F6', // Blue theme color
  },
});

// Helper functions
export const formatCurrency = (amount: number, currency: SupportedCurrency): string => {
  const currencyInfo = SUPPORTED_CURRENCIES.find(c => c.code === currency);
  if (!currencyInfo) return `${amount} ${currency}`;

  return `${currencyInfo.symbol}${amount.toLocaleString()}`;
};

export const getCurrencySymbol = (currency: SupportedCurrency): string => {
  const currencyInfo = SUPPORTED_CURRENCIES.find(c => c.code === currency);
  return currencyInfo?.symbol || currency;
};

export const getPlanPrice = (
  planId: string,
  currency: SupportedCurrency,
  billing: 'monthly' | 'yearly'
): number => {
  const planPricing = PLAN_PRICING[planId.toLowerCase()];
  if (!planPricing) return 0;

  const currencyPricing = planPricing[currency];
  if (!currencyPricing) return 0;

  return currencyPricing[billing];
};

// Razorpay payment methods based on currency
// INR supports: UPI, Netbanking, Card, Wallet, EMI
// USD supports: Card only
export const getPaymentMethods = (currency: SupportedCurrency): string[] => {
  if (currency === 'INR') {
    return ['upi', 'netbanking', 'card', 'wallet']; // Multiple methods for INR
  }
  return ['card']; // International cards only for USD
};

// Check if Razorpay is properly configured
export const isRazorpayConfigured = (): boolean => {
  return Boolean(RAZORPAY_KEY_ID);
};

// Error messages for different scenarios
export const RAZORPAY_ERRORS = {
  NOT_CONFIGURED: 'Payment system is not properly configured. Please contact support.',
  PAYMENT_FAILED: 'Payment failed. Please try again or contact support.',
  NETWORK_ERROR: 'Network error occurred. Please check your connection and try again.',
  INVALID_AMOUNT: 'Invalid payment amount. Please refresh and try again.',
  USER_CANCELLED: 'Payment was cancelled by user.',
} as const;